import type { ConnectionType, DiagramDocument, DiagramEdge, DiagramNode, DeviceType } from '@ukrop-nm/shared';
import { createEmptyDiagram, DEVICE_TYPE_LABELS_UK } from '@ukrop-nm/shared';
import { extractDrawioXml, expandCompressedDrawioXml } from './extract.js';

export { extractDrawioXml, decompressMxDiagram, expandCompressedDrawioXml } from './extract.js';
export { exportDrawioXml } from './exportDrawio.js';

export interface DrawioImportWarning {
  code: string;
  message: string;
  cellId?: string;
}

export interface DrawioImportResult {
  document: DiagramDocument;
  warnings: DrawioImportWarning[];
}

function decodeXmlEntities(input: string): string {
  return input
    .replace(/&#xa;/gi, '\n')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&');
}

function stripHtml(input: string): string {
  return decodeXmlEntities(input)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .trim();
}

function styleParam(style: string, key: string): string | undefined {
  const m = new RegExp(`(?:^|;)${key}=([^;]*)`, 'i').exec(style);
  const v = m?.[1]?.trim();
  return v ? decodeXmlEntities(v) : undefined;
}

const CONNECTION_TYPES = new Set<ConnectionType>([
  'ethernet',
  'fiber',
  'wifi',
  'wan',
  'sip',
  'logical',
  'unknown',
]);

export function guessType(style: string, value: string): DeviceType {
  const tagged = styleParam(style, 'ukropType');
  if (tagged && tagged in DEVICE_TYPE_LABELS_UK) return tagged as DeviceType;
  const s = `${style} ${value}`.toLowerCase();
  if (s.includes('swimlane') || s.includes('zone')) return 'zone';
  if (s.includes('firewall') || s.includes('asa') || s.includes('security')) return 'firewall';
  if (s.includes('starlink') || s.includes('vsat') || s.includes('satellite')) return 'satellite';
  if (
    (s.includes('lte') || s.includes('4g') || s.includes('5g')) &&
    (s.includes('dish') ||
      s.includes('antenna') ||
      s.includes('lhg') ||
      s.includes('sxt') ||
      s.includes('mimo') ||
      s.includes('panel') ||
      s.includes('тарел') ||
      s.includes('антен'))
  ) {
    return 'lte_antenna';
  }
  if (s.includes('lhg lte') || s.includes('sxt lte')) return 'lte_antenna';
  if (s.includes('dish')) return 'satellite';
  if (s.includes('modem') || s.includes('ont') || s.includes('transport')) return 'modem';
  if (
    s.includes('access point') ||
    s.includes('accesspoint') ||
    s.includes('точка доступу') ||
    s.includes('unifi') ||
    /\b(cap|wap)\b/.test(s)
  ) {
    if (s.includes('phone')) return 'wifi_phone';
    return 'access_point';
  }
  if (s.includes('wifi') || s.includes('wlan') || s.includes('wireless')) {
    if (s.includes('phone')) return 'wifi_phone';
    return 'wifi_router';
  }
  if (s.includes('switch')) return 'switch';
  if (s.includes('controller') || s.includes('module')) return 'controller';
  if (s.includes('router')) return 'router';
  if (s.includes('server') || s.includes('rack')) return 'server';
  if (s.includes('laptop') || s.includes('notebook') || s.includes('vivobook')) return 'laptop';
  if (s.includes('tablet')) return 'tablet';
  if (
    s.includes('telepresence') ||
    s.includes('display') ||
    s.includes('monitor') ||
    s.includes('tv')
  ) {
    return 'display';
  }
  if (s.includes('phone') || s.includes('sip')) return 'ip_phone';
  if (s.includes('printer') || s.includes('mfp')) return 'printer';
  if (s.includes('camera') || s.includes('cctv') || s.includes('nvr')) return 'camera';
  if (s.includes('nas') || s.includes('storage') || s.includes('disk')) return 'nas';
  if (s.includes('cloud') || s.includes('internet')) return 'cloud';
  if (
    s.includes('ecoflow') ||
    s.includes('bluetti') ||
    s.includes('jackery') ||
    s.includes('power station') ||
    s.includes('powerstation') ||
    s.includes('зарядн') ||
    s.includes('портативн')
  ) {
    return 'power_station';
  }
  if (s.includes('ups') || s.includes('дбж') || s.includes('power')) return 'ups';
  if (s.includes('iot') || s.includes('sensor')) return 'iot';
  if (s.includes('pc') || s.includes('desktop') || s.includes('workstation') || /\bпк\b/i.test(value)) {
    return 'desktop';
  }
  return 'generic';
}

function shouldSkipVertex(style: string, value: string): boolean {
  if (style.includes('text;') && !style.includes('shape=') && !styleParam(style, 'ukropType')) return true;
  if (
    !value &&
    !style.includes('swimlane') &&
    !style.includes('shape=') &&
    !styleParam(style, 'ukropType')
  ) {
    return true;
  }
  if (style.includes('fillColor=none') && style.includes('strokeColor=none')) return true;
  return false;
}

/**
 * Local parser for mxfile / mxGraphModel.
 * Original .drawio is never uploaded to Cloudflare.
 */
export function importDrawioXml(xml: string, siteId = 'SITE-DEMO'): DrawioImportResult {
  const warnings: DrawioImportWarning[] = [];
  const doc = createEmptyDiagram(siteId, 'Імпорт draw.io');
  const expanded = expandCompressedDrawioXml(xml);

  if (expanded.includes('mxfile') === false && expanded.includes('mxGraphModel') === false) {
    warnings.push({ code: 'NOT_DRAWIO', message: 'Файл не схожий на draw.io XML' });
    return { document: doc, warnings };
  }

  if (expanded === xml && (xml.includes('compressed="true"') || /<diagram[^>]*>[A-Za-z0-9+/=]{80,}/.test(xml))) {
    warnings.push({
      code: 'COMPRESSED_LIMITED',
      message: 'Не вдалося розпакувати стиснений draw.io. Збережіть у diagrams.net як Uncompressed XML.',
    });
  }

  const parser = new DOMParser();
  const dom = parser.parseFromString(expanded, 'application/xml');
  if (dom.querySelector('parsererror')) {
    warnings.push({ code: 'PARSE_ERROR', message: 'Не вдалося розпарсити XML' });
    return { document: doc, warnings };
  }

  const cells = Array.from(dom.querySelectorAll('mxCell'));
  const nodes: DiagramNode[] = [];
  const edges: DiagramEdge[] = [];
  const idMap = new Map<string, string>();
  const parentAbs = new Map<string, { x: number; y: number }>();

  // First pass: zones/swimlanes for absolute coords
  for (const cell of cells) {
    const id = cell.getAttribute('id') || '';
    const vertex = cell.getAttribute('vertex') === '1';
    const style = cell.getAttribute('style') || '';
    const value = stripHtml(cell.getAttribute('value') || '');
    const geometry = cell.querySelector('mxGeometry');
    if (!vertex || !geometry || !style.includes('swimlane')) continue;
    if (shouldSkipVertex(style, value)) continue;

    const x = Number(geometry.getAttribute('x') || 0);
    const y = Number(geometry.getAttribute('y') || 0);
    const width = Number(geometry.getAttribute('width') || 320);
    const height = Number(geometry.getAttribute('height') || 220);
    const nodeId = crypto.randomUUID();
    idMap.set(id, nodeId);
    parentAbs.set(id, { x, y });
    nodes.push({
      id: nodeId,
      monitorKey: '',
      type: 'zone',
      label: value || 'Зона',
      description: '',
      zoneId: null,
      position: { x, y },
      size: { width, height },
      criticality: 'low',
      monitorMode: 'passive',
      expectedPresence: 'always',
      notes: '',
      locked: false,
    });
  }

  for (const cell of cells) {
    const id = cell.getAttribute('id') || crypto.randomUUID();
    const vertex = cell.getAttribute('vertex') === '1';
    const edge = cell.getAttribute('edge') === '1';
    const style = cell.getAttribute('style') || '';
    const value = stripHtml(cell.getAttribute('value') || '');
    const geometry = cell.querySelector('mxGeometry');
    const parent = cell.getAttribute('parent') || '';

    if (vertex && geometry) {
      if (style.includes('swimlane')) continue;
      if (shouldSkipVertex(style, value)) continue;
      if (!style.includes('shape=') && !value && !styleParam(style, 'ukropType')) continue;

      const x = Number(geometry.getAttribute('x') || 0);
      const y = Number(geometry.getAttribute('y') || 0);
      const width = Number(geometry.getAttribute('width') || 72);
      const height = Number(geometry.getAttribute('height') || 72);
      const type = guessType(style, value);
      if (type === 'generic' && !style.includes('shape=') && !styleParam(style, 'ukropType') && value.length < 2) {
        continue;
      }

      const nodeId = crypto.randomUUID();
      idMap.set(id, nodeId);

      const origin = parentAbs.get(parent) ?? { x: 0, y: 0 };
      const absX = x + origin.x;
      const absY = y + origin.y;
      const ukropKey = styleParam(style, 'ukropKey');

      nodes.push({
        id: nodeId,
        monitorKey: type === 'zone' ? '' : ukropKey || `IMP-${id.slice(0, 8)}`,
        type,
        label: value.split('\n')[0] || ukropKey || `IMP-${id.slice(0, 8)}`,
        description: value.includes('\n') ? value : '',
        zoneId: parent && idMap.has(parent) ? idMap.get(parent)! : null,
        position: { x: absX, y: absY },
        size: { width, height },
        criticality: type === 'router' || type === 'switch' || type === 'firewall' ? 'critical' : 'normal',
        monitorMode:
          type === 'switch' || type === 'zone'
            ? 'passive'
            : type === 'laptop' || type === 'desktop' || type === 'display'
              ? 'dhcp_presence'
              : 'icmp',
        expectedPresence:
          type === 'laptop' || type === 'desktop' || type === 'wifi_phone' ? 'optional' : 'always',
        notes: '',
        locked: false,
        needsReview: type === 'generic',
      });

      if (type === 'generic') {
        warnings.push({
          code: 'UNKNOWN_SHAPE',
          message: `Невідома фігура → generic: ${value || id}`,
          cellId: id,
        });
      }
    }

    if (edge) {
      const source = cell.getAttribute('source');
      const target = cell.getAttribute('target');
      const sourceNodeId = source ? idMap.get(source) : undefined;
      const targetNodeId = target ? idMap.get(target) : undefined;
      if (!sourceNodeId || !targetNodeId) {
        warnings.push({
          code: 'EDGE_UNBOUND',
          message: `Зʼєднання без кінців або до підпису: ${id}`,
          cellId: id,
        });
        continue;
      }

      const sourceNode = nodes.find((n) => n.id === sourceNodeId);
      const targetNode = nodes.find((n) => n.id === targetNodeId);
      const needsReview = sourceNode?.type === 'zone' || targetNode?.type === 'zone';
      if (needsReview) {
        warnings.push({
          code: 'EDGE_TO_ZONE',
          message: `Зʼєднання привʼязане до зони — потрібен review`,
          cellId: id,
        });
      }

      const taggedConn = styleParam(style, 'ukropConn');
      const connectionType: ConnectionType =
        taggedConn && CONNECTION_TYPES.has(taggedConn as ConnectionType)
          ? (taggedConn as ConnectionType)
          : /rf|radio|sat/i.test(value)
            ? 'wifi'
            : /lan|eth|fiber|опт/i.test(value)
              ? 'ethernet'
              : 'unknown';

      edges.push({
        id: crypto.randomUUID(),
        sourceNodeId,
        targetNodeId,
        sourcePort: styleParam(style, 'ukropPortS'),
        targetPort: styleParam(style, 'ukropPortT'),
        connectionType,
        monitorSource: 'unknown',
        label: value || undefined,
        networkCidr: styleParam(style, 'ukropCidr'),
        criticality: 'normal',
        needsReview: Boolean(needsReview),
      });
    }
  }

  doc.name = 'Схема ПУ (імпорт)';
  doc.nodes = nodes;
  doc.edges = edges;
  doc.updatedAt = new Date().toISOString();
  return { document: doc, warnings };
}

export function importDrawioFile(fileText: string, siteId?: string, filename?: string): DrawioImportResult {
  const xml = extractDrawioXml({ text: fileText, filename }) ?? fileText;
  return importDrawioXml(xml, siteId);
}

export function importDrawioBytes(
  bytes: Uint8Array,
  filename: string,
  siteId?: string,
): DrawioImportResult {
  const xml = extractDrawioXml({ bytes, filename });
  if (!xml) {
    const doc = createEmptyDiagram(siteId ?? 'SITE-DEMO', 'Імпорт draw.io');
    return {
      document: doc,
      warnings: [
        {
          code: 'NOT_DRAWIO',
          message:
            'У файлі немає XML draw.io. Для PNG/SVG збережіть у diagrams.net з увімкненою вбудованою схемою, або імпортуйте .drawio / .xml.',
        },
      ],
    };
  }
  return importDrawioXml(xml, siteId);
}
